import React from "react";
import { AlertCircle, AlertTriangle, CheckCircle2, Info } from "lucide-react";
import cn from "./cn";

/**
 * Callout — a tinted, bordered notice inside a page or a dialog.
 *
 *   <Callout tone="warning">Everything here is written to the audit log.</Callout>
 *   <Callout tone="danger" role="alert">{error}</Callout>
 *   <Callout tone="danger" icon={<Radio size={14} />}>…</Callout>
 *
 * Built because ONE screen — the support-login picker — carried five of these in
 * three tones, at five different paddings, two radii, three tint alphas and three
 * border alphas (TOKEN_CONTRACT §11; ledger row D8 asked for exactly this evidence
 * before it was built). None of that variation meant anything; it is what
 * hand-writing the same box five times produces.
 *
 * ── THE ICON IS NOT DECORATION ───────────────────────────────────────────────
 * There is deliberately NO way to suppress it. A callout whose tone is carried by
 * its tint and border alone conveys meaning by colour only, which fails WCAG
 * 1.4.1 — and that is exactly what the `__error` box this replaces did. Pass
 * `icon` to swap the glyph for a more specific one; the default is the tone's.
 *
 * `role` is an explicit prop rather than derived from the tone. A `role="alert"`
 * is for a message that ARRIVES in response to something the user did; the same
 * tone is also used for a standing warning that is on screen from mount, and
 * announcing that one on every re-render is noise. The call site knows which it is.
 *
 * ⚠️ **NOT named `Alert`, deliberately.** `react-bootstrap` exports one, and **40
 * files in `src/` already render `<Alert variant="danger">`**. Two components with
 * one name, one import line apart, is the trap `AppButton`'s alias map exists to
 * clean up after — and here it would be silent in the worse direction: this one
 * takes `tone`, so a pasted `variant="danger"` would spread onto the div and the
 * box would come out `info`-blue. `Callout` cannot be confused with either.
 * Those 40 are the population this replaces, module by module.
 *
 * Props:
 *   tone      — "info" (default) | "success" | "warning" | "danger"
 *   icon      — node, overriding the tone's default glyph
 *   role      — pass "alert" for a message that appears in response to an action
 *   action    — node at the trailing edge: the one thing to do about the
 *               message ("Send reminder"), usually an `AppButton size="xs"`
 *   children  — the message
 */

const CALLOUT_BASE_CLASSES =
  // `tw-border-solid` is not decoration — Preflight is off, so a bare `tw-border`
  // is a width on a border whose style is still `none`, and it paints nothing.
  // Same note PanelButton carries, same reason.
  "tw-flex tw-items-start tw-gap-2 tw-rounded-sm tw-border tw-border-solid " +
  "tw-px-3 tw-py-2.5 tw-text-xs tw-leading-normal tw-text-content";

// One tint alpha and one border alpha across all four, which is the point: the
// tone is the hue, not a density. The ink stays `--text-content` — a family's base
// token measures 2.5–3.1:1 on a white card (b26), so body copy tinted with it
// would be less readable than the page around it, not more.
const CALLOUT_TONE_CLASSES = {
  info: "tw-border-info/30 tw-bg-info/10",
  success: "tw-border-success/30 tw-bg-success/10",
  warning: "tw-border-warning/30 tw-bg-warning/10",
  danger: "tw-border-danger/30 tw-bg-danger/10",
};

const CALLOUT_ICON_CLASSES = {
  info: "tw-text-info",
  success: "tw-text-success",
  warning: "tw-text-warning",
  danger: "tw-text-danger",
};

const CALLOUT_DEFAULT_ICONS = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  danger: AlertCircle,
};

const warnedTones = new Set();

/** An unknown tone would otherwise render an untinted, unbordered box that still
 *  claims to be an alert — the silently-wrong shape this library exists to remove. */
const resolveTone = (tone) => {
  if (Object.hasOwn(CALLOUT_TONE_CLASSES, tone)) return tone;
  if (process.env.NODE_ENV !== "production" && !warnedTones.has(tone)) {
    warnedTones.add(tone);
    // eslint-disable-next-line no-console
    console.warn(
      `[Callout] unknown tone "${tone}" — falling back to "info". ` +
        `Valid tones: ${Object.keys(CALLOUT_TONE_CLASSES).join(", ")}.`
    );
  }
  return "info";
};

const Callout = ({ tone = "info", icon, role, action, className = "", children, ...props }) => {
  const resolved = resolveTone(tone);
  const DefaultIcon = CALLOUT_DEFAULT_ICONS[resolved];

  return (
    <div
      role={role}
      className={cn(CALLOUT_BASE_CLASSES, CALLOUT_TONE_CLASSES[resolved], className)}
      {...props}
    >
      {/* `tw-mt-px` rather than centring: these run to several lines, and an icon
          vertically centred against a paragraph reads as floating in the middle
          of the text. It aligns to the first line. */}
      <span className={cn("tw-mt-px tw-shrink-0", CALLOUT_ICON_CLASSES[resolved])} aria-hidden="true">
        {icon || <DefaultIcon size={15} />}
      </span>
      <span className="tw-min-w-0 tw-flex-1">{children}</span>
      {action && <span className="tw-shrink-0 tw-self-center">{action}</span>}
    </div>
  );
};

export default Callout;
